import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Grid, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, tableCellClasses } from '@mui/material';
import styled_ from '@emotion/styled';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { usePermissions } from '../../hooks/usePermissions';
import { approveEmployeeWeeklyTimesheet, fetchEmployeeTimesheetMonthly } from '../../store/payrollStore/payrollThunk';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#9da7b7',
    borderTop: 'none',
    borderBottom: '0.25px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: 13,
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: 14,
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: 'none',
    paddingLeft: '15px !important',
    margin: 0,
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': { backgroundColor: '#fbfbfb' },
  '&:nth-of-type(odd)': { backgroundColor: '#e9ecf1' },
  '&:last-child td, &:last-child th': {},
}));

const typeColor: Record<string, { bg: string; fg: string }> = {
  Present: { bg: '#edf6ef', fg: '#49a563' },
  'Half-Day': { bg: '#fff7ed', fg: '#c2410c' },
  Overtime: { bg: '#e0f2fe', fg: '#0369a1' },
  WFH: { bg: '#ede9fe', fg: '#5b21b6' },
  Absent: { bg: '#fbecec', fg: '#c33b3b' },
  Leave: { bg: '#fbecec', fg: '#c33b3b' },
  Holiday: { bg: '#fce7f3', fg: '#9d174d' },
  'Optional-Holiday': { bg: '#fce7f3', fg: '#9d174d' },
  WeekOff: { bg: '#f3f4f6', fg: '#374151' },
  Scheduled: { bg: '#f3f4f6', fg: '#6b7280' },
};

interface WeekRow {
  timesheetId: string;
  weekStartDate: string;
  weekEndDate: string;
  status: string;
  weekNumber: number;
  month: number;
  year: number;
  title?: string;
  presentDays: number;
  halfDays: number;
  overtimeDays: number;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalWorkedHours: number;
  rejectedComments?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName?: string;
  weekRow: WeekRow | null;
  onActionDone?: (newStatus: string) => void;
}

const EmployeeTimesheetWeekDrawer: FC<Props> = ({ open, onClose, employeeId, employeeName, weekRow, onActionDone }) => {
  const dispatch = useAppDispatch();
  const { isAdmin, getUserRole } = usePermissions();
  const currentUser = useAppSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState<any[]>([]);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | 'pending' | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectComments, setRejectComments] = useState('');

  useEffect(() => {
    if (weekRow?.status) setCurrentStatus(weekRow.status);
  }, [weekRow]);

  useEffect(() => {
    if (!open || !weekRow || !employeeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Monthly endpoint returns all days for the month — we filter to this week.
        // Fetch both months if the week spans two months (rare but possible).
        const startD = new Date(weekRow.weekStartDate);
        const endD = new Date(weekRow.weekEndDate);
        const startKey = `${startD.getFullYear()}-${startD.getMonth() + 1}`;
        const endKey = `${endD.getFullYear()}-${endD.getMonth() + 1}`;

        const calls = [dispatch(fetchEmployeeTimesheetMonthly({ employeeId, year: startD.getFullYear(), month: startD.getMonth() + 1 })).unwrap()];
        if (startKey !== endKey) {
          calls.push(dispatch(fetchEmployeeTimesheetMonthly({ employeeId, year: endD.getFullYear(), month: endD.getMonth() + 1 })).unwrap());
        }
        const results: any[] = await Promise.all(calls);
        if (cancelled) return;

        const all: any[] = [];
        for (const r of results) {
          if (Array.isArray(r?.days)) all.push(...r.days);
        }
        // Filter to days inside the selected week
        const startMs = startD.getTime();
        const endMs = endD.getTime() + 24 * 60 * 60 * 1000 - 1;
        const filtered = all.filter((d: any) => {
          if (!d.date) return false;
          const t = new Date(d.date).getTime();
          return t >= startMs && t <= endMs;
        });
        filtered.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setDays(filtered);
      } catch (e) {
        if (!cancelled) setDays([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch, open, weekRow, employeeId]);

  const handleAction = async (action: 'approve' | 'reject' | 'pending', comments?: string) => {
    if (!weekRow || !currentUser?._id) return;
    setActionLoading(action);
    try {
      const res: any = await dispatch(approveEmployeeWeeklyTimesheet({
        approverId: currentUser._id,
        employeeId,
        timesheetId: weekRow.timesheetId,
        action,
        rejectedComments: comments,
        userRole: getUserRole(),
      })).unwrap();
      const newStatus = res?.status || (action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Pending');
      setCurrentStatus(newStatus);
      onActionDone?.(newStatus);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Action failed';
      alert(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectDialog = () => {
    setRejectComments(weekRow?.rejectedComments || '');
    setRejectDialogOpen(true);
  };

  const submitReject = async () => {
    if (!rejectComments.trim()) {
      alert('Please add rejection comments.');
      return;
    }
    setRejectDialogOpen(false);
    await handleAction('reject', rejectComments.trim());
  };

  const totals = useMemo(() => {
    return days.reduce(
      (acc: any, d: any) => {
        const reg = Number(d.regularHours || 0);
        const ot = Number(d.overtimeHours || 0);
        acc.regular += reg;
        acc.overtime += ot;
        acc.total += Number(d.hours) || (reg + ot);
        if (d.type === 'Present') acc.present += 1;
        if (d.type === 'Half-Day') acc.half += 1;
        if (d.type === 'Absent') acc.absent += 1;
        if (d.type === 'Leave') acc.leave += 1;
        return acc;
      },
      { regular: 0, overtime: 0, total: 0, present: 0, half: 0, absent: 0, leave: 0 }
    );
  }, [days]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} elevation={0} PaperProps={{ elevation: 0, sx: { width: { xs: '100vw', md: 720 }, p: 0, boxShadow: 'none !important', borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', backgroundImage: 'none' } }}>
      {/* Header — same style as Record Expense Payment */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #eee', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#292d35' }}>Weekly Timesheet</Typography>
          {weekRow && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                {employeeName ? `${employeeName} · ` : ''}
                {moment(weekRow.weekStartDate).format('DD MMM')} – {moment(weekRow.weekEndDate).format('DD MMM YYYY')}
                {' · W'}{weekRow.weekNumber}
              </Typography>
              <Chip label={currentStatus || weekRow.status} size="small" sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="close"
            sx={{
              border: '1px solid #cbd5e1',
              color: '#545454',
              backgroundColor: '#fff',
              width: 32,
              height: 32,
              '&:hover': { backgroundColor: '#f3f4f6', borderColor: '#9da7b7' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
        {/* Summary cards */}
        {weekRow && (
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Present', val: totals.present || weekRow.presentDays },
              { label: 'Half-day', val: totals.half || weekRow.halfDays },
              { label: 'Absent', val: totals.absent },
              { label: 'Leave', val: totals.leave },
              { label: 'Regular Hrs', val: (totals.regular || weekRow.totalRegularHours || 0).toFixed(2) },
              { label: 'OT Hrs', val: (totals.overtime || weekRow.totalOvertimeHours || 0).toFixed(2) },
            ].map((s, i) => (
              <Grid item xs={4} sm={2} key={i}>
                <Box sx={{ p: 1.25, border: '1px solid #cbd5e1', borderRadius: 1.5, textAlign: 'center', backgroundColor: '#fff', boxShadow: 'none', backgroundImage: 'none' }}>
                  <Typography variant="caption" sx={{ color: '#9da7b7', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10 }}>{s.label}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#292d35' }}>{s.val}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {weekRow?.rejectedComments && (
          <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #fecaca', backgroundColor: '#fef2f2', borderRadius: 1.5, boxShadow: 'none', backgroundImage: 'none' }}>
            <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600 }}>Rejection Comments</Typography>
            <Typography variant="body2" sx={{ color: '#7f1d1d' }}>{weekRow.rejectedComments}</Typography>
          </Paper>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        ) : days.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#9da7b7' }}>
            <Typography variant="body2">No attendance entries for this week.</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }} className={'c-card2'}>
            <Table stickyHeader sx={{ width: '100%' }} size="small" aria-label="week attendance table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell>Day</StyledTableCell>
                  <StyledTableCell>Check In</StyledTableCell>
                  <StyledTableCell>Check Out</StyledTableCell>
                  <StyledTableCell>Reg / OT</StyledTableCell>
                  <StyledTableCell>Total</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map((d: any, i: number) => {
                  const c = typeColor[d.type] || { bg: '#f3f4f6', fg: '#374151' };
                  return (
                    <StyledTableRow key={i}>
                      <StyledTableCell>{d.date ? moment(d.date).format('DD MMM') : '-'}</StyledTableCell>
                      <StyledTableCell>{d.day || (d.date ? moment(d.date).format('ddd') : '-')}</StyledTableCell>
                      <StyledTableCell>{d.checkIn ? moment(d.checkIn).format('hh:mm A') : '-'}</StyledTableCell>
                      <StyledTableCell>{d.checkOut ? moment(d.checkOut).format('hh:mm A') : '-'}</StyledTableCell>
                      <StyledTableCell>{Number(d.regularHours || 0).toFixed(2)} / {Number(d.overtimeHours || 0).toFixed(2)}</StyledTableCell>
                      <StyledTableCell><span style={{ fontWeight: 600 }}>{(Number(d.hours) || (Number(d.regularHours || 0) + Number(d.overtimeHours || 0))).toFixed(2)}</span></StyledTableCell>
                      <StyledTableCell>
                        <Chip size="small" style={{ borderRadius: 7, color: c.fg, backgroundColor: c.bg }} label={d.type || '-'} />
                        {d.label ? <div style={{ fontSize: 11, color: '#9da7b7', marginTop: 2 }}>{d.label}</div> : null}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>


      {/* Bottom Cancel button — moved to header as close icon */}
      {/*
      <Box sx={{
        backgroundColor: '#fff',
        borderTop: '1px solid #c1c5cb',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ minWidth: '100px', height: '40px' }}
        >
          Cancel
        </Button>
      </Box>
      */}

      {/* Admin action footer — temporarily disabled */}
      {/*
      {isAdmin() && weekRow && (
        <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {currentStatus !== 'Pending' && currentStatus !== 'Open' && (
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              startIcon={actionLoading === 'pending' ? <CircularProgress size={14} /> : <RestartAltIcon />}
              disabled={!!actionLoading}
              onClick={() => handleAction('pending')}
              sx={{ textTransform: 'none' }}
            >
              Reopen
            </Button>
          )}
          {currentStatus !== 'Rejected' && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={actionLoading === 'reject' ? <CircularProgress size={14} /> : <CancelIcon />}
              disabled={!!actionLoading}
              onClick={openRejectDialog}
              sx={{ textTransform: 'none' }}
            >
              Reject
            </Button>
          )}
          {currentStatus !== 'Approved' && (
            <Button
              variant="contained"
              size="small"
              color="success"
              startIcon={actionLoading === 'approve' ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckCircleIcon />}
              disabled={!!actionLoading}
              onClick={() => handleAction('approve')}
              sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
            >
              Approve
            </Button>
          )}
        </Box>
      )}
      */}

      {/* Reject reason dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, boxShadow: 'none', border: '1px solid #e5e7eb' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Reject Timesheet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1.5 }}>
            Add a reason so the employee knows what to fix.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder="Rejection comments"
            value={rejectComments}
            onChange={(e) => setRejectComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} variant="outlined" color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={submitReject} variant="contained" color="error" sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
            Reject Timesheet
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default EmployeeTimesheetWeekDrawer;
